"use client";
import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { FaEye, FaTimes, FaGraduationCap, FaClock, FaCertificate, FaArrowRight, FaUser } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { generateSlug } from "@/lib/utils";
import MemberImage from "@/app/components/Teams/MemberImage";

interface TeamMember {
  name: string;
  role: string;
  subRole?: string;
  image: string;
  qualification: string;
  experience: string;
  certifications: string[];
}

interface TeamCardProps { member: TeamMember; }

export default function TeamCard({ member }: TeamCardProps) {
  const [open, setOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  return (
    <>
      <motion.div 
        className={`group relative bg-white text-center rounded-2xl p-4 space-y-3 shadow-lg overflow-hidden transition-all duration-300 w-full max-w-[420px] h-[530px] hover:shadow-xl ${
          member.certifications?.length > 0 
            ? 'cursor-pointer' 
            : 'cursor-default'
        }`}
        onClick={() => member.certifications?.length > 0 && setOpen(true)}
        whileHover={member.certifications?.length > 0 ? { y: -5, scale: 1.02 } : {}}
        whileTap={member.certifications?.length > 0 ? { scale: 0.98 } : {}}
      >
        {/* Image Section */}
        <div className="relative h-92">
          <MemberImage
            name={member.name}
            image={member.image}
            className="object-cover object-top"
            initialsClassName="text-white text-5xl font-bold"
          />

          {/* Bottom Curve - SVG Carve */}
          <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] z-10">
            <svg
              viewBox="0 0 500 50"
              preserveAspectRatio="none"
              className="w-full h-[60px]"
            >
              <path
                d="M0,0 C150,60 350,60 500,0 L500,50 L0,50 Z"
                className="fill-white"
              />
            </svg>
          </div>
        </div>
        
        {/* Info & Eye Icon - overlapping with curve */}
        <div className="relative -mt-3 px-5 pb-8 pt-8 z-30 bg-white">
          <div className="flex items-center justify-between">
            {/* Text Info */}
            <div className="text-left">
              <h2 className="text-xl font-normal text-gray-800 leading-snug">
                {member.name}
              </h2>
              <p className="text-sm text-purple-600">{member.role}</p>
              {member.subRole && (
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{member.subRole}</p>
              )}
            </div>
            {/* Floating Action Buttons */}
            <div className="absolute -top-13 right-4 flex gap-2 z-20">
              {member.certifications?.length > 0 && (
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                  }}
                  className="w-11 h-11 rounded-full bg-gradient-to-r from-[#6E1FB8] to-[#DA159B] text-white shadow-lg backdrop-blur-md flex items-center justify-center hover:scale-105 transition-all duration-300 cursor-pointer"
                  aria-label="View Details"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaEye className="text-xl" />
                </motion.button>
              )}
              
              <Link 
                href={`/teams/member/${generateSlug(member.name)}`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="block"
              >
                <motion.button
                  className="w-11 h-11 rounded-full bg-gradient-to-r from-[#54169C] to-pink-500 text-white shadow-lg backdrop-blur-md flex items-center justify-center hover:scale-105 transition-all duration-300 cursor-pointer"
                  aria-label="View Profile"
                  whileHover={{ scale: 1.1, rotate: -5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaArrowRight className="text-lg" />
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Gradient underline on hover */}
        <div className="absolute bottom-0 left-0 h-2 w-0 group-hover:w-full transition-all duration-700 ease-out bg-gradient-to-r from-[#DA159B] to-[#54169C] z-20 rounded-t-full group-hover:shadow-lg" />
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div 
            className="fixed inset-0 z-50 flex justify-center items-center px-4 py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Backdrop */}
            <motion.div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            
            {/* Modal */}
            <motion.div 
              ref={modalRef}
              className="relative w-full max-w-4xl max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {/* Close Button */}
              <motion.button
                className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm text-gray-800 hover:bg-white transition-all duration-300 flex items-center justify-center shadow-lg cursor-pointer"
                onClick={() => setOpen(false)}
                aria-label="Close details"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTimes className="text-sm" aria-hidden="true" />
              </motion.button>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(80vh-48px)]">
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* Left Column - Image & Basic Info */}
                    <div className="lg:col-span-1 space-y-4">
                      {/* Image */}
                      <div className="relative group">
                        <div className="relative w-full aspect-[3/4] rounded-xl overflow-hidden shadow-lg">
                          <MemberImage
                            name={member.name}
                            image={member.image}
                            className="object-cover transition-transform duration-500 group-hover:scale-110"
                            initialsClassName="text-white text-4xl font-bold"
                          />
                        </div>
                      </div>

                      {/* Name & Role */}
                      <div className="text-center p-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl border border-purple-200 shadow-sm">
                        <h2 className="text-xl font-bold text-gray-800 mb-1">
                          {member.name}
                        </h2>
                        <p className="text-sm text-purple-600 font-medium">
                          {member.role}
                        </p>
                        {member.subRole && (
                          <p className="text-xs text-gray-600 mt-1 font-normal">
                            {member.subRole}
                          </p>
                        )}
                      </div>

                      {/* Learn More Button */}
                      <div className="pt-2">
                        <Link 
                          href={`/teams/member/${generateSlug(member.name)}`}
                          className="block w-full"
                        >
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full px-6 py-3 bg-gradient-to-r from-[#54169C] to-[#DA159B] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer group"
                          >
                            <span className="pointer-events-none">Learn More</span>
                            <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform duration-300 pointer-events-none" />
                          </motion.div>
                        </Link>
                      </div>
                    </div>

                    {/* Right Column - Details */}
                    <div className="lg:col-span-2 space-y-4">
                      
                      {/* Qualification & Experience Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Qualification */}
                        {member.qualification && (
                          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-8 h-8 bg-gradient-to-r from-[#54169C] to-[#DA159B] rounded-full flex items-center justify-center">
                                <FaGraduationCap className="text-white text-sm" />
                              </div>
                              <h3 className="text-base font-semibold text-gray-800">Qualification</h3>
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed">{member.qualification}</p>
                          </div>
                        )}

                        {/* Experience */}
                        {member.experience && (
                          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-100">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                                <FaClock className="text-white text-sm" />
                              </div>
                              <h3 className="text-base font-semibold text-gray-800">Experience</h3>
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed">{member.experience}</p>
                          </div>
                        )}
                      </div>

                      {/* Certifications */}
                      {member.certifications?.length > 0 && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                              <FaCertificate className="text-white text-sm" />
                            </div>
                            <h3 className="text-base font-semibold text-gray-800">Certifications</h3>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {member.certifications.map((cert, idx) => (
                              <div 
                                key={idx}
                                className="flex items-center gap-2 p-2 bg-white/60 rounded-lg"
                              >
                                <div className="w-4 h-4 bg-gradient-to-r from-[#DA159B] to-[#54169C] rounded-full flex items-center justify-center flex-shrink-0">
                                  <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <span className="text-gray-700 text-sm font-medium">{cert}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
